import React from 'react';

interface BPIScoreCardProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  onClick?: () => void;
}

/**
 * BPI Score Badge Component
 * Similar to CPIScoreBadge but for Barber Performance Index
 *
 * Score ranges:
 * - 0-50: Red (Below Standard)
 * - 51-75: Yellow (Standard)
 * - 76-100: Green (High Performer)
 */
export const BPIScoreCard: React.FC<BPIScoreCardProps> = ({
  score,
  size = 'md',
  showLabel = true,
  onClick
}) => {
  // Clamp score to 0-100
  const clampedScore = Math.min(100, Math.max(0, Math.round(score)));

  // Determine color and label based on score
  const getScoreDisplay = () => {
    if (clampedScore >= 76) {
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300',
        label: 'High Performer',
        icon: '⭐'
      };
    } else if (clampedScore >= 51) {
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300',
        label: 'Standard',
        icon: '👤'
      };
    } else {
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300',
        label: 'Needs Improvement',
        icon: '⚠️'
      };
    }
  };

  const display = getScoreDisplay();

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 rounded-full border
        ${display.bgColor} ${display.textColor} ${display.borderColor}
        ${sizeClasses[size]}
        ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
      `}
      onClick={onClick}
      title={`BPI Score: ${clampedScore}/100 - ${display.label}`}
    >
      <span className={iconSizes[size]}>{display.icon}</span>
      <span className="font-semibold">{clampedScore}</span>
      {showLabel && size !== 'sm' && (
        <span className="font-medium">{display.label}</span>
      )}
    </div>
  );
};

/**
 * Detailed BPI Score Card (for profile view)
 * Shows score breakdown and performance metrics
 */
interface BPIDetailedCardProps {
  score: number;
  breakdown: {
    serviceCount: number;  // 0-100 (25% weight)
    revenue: number;       // 0-100 (25% weight)
    speed: number;         // 0-100 (20% weight)
    satisfaction: number;  // 0-100 (20% weight)
    rebooking: number;     // 0-100 (10% weight)
  };
  rank?: string;
  level?: number;
  percentile?: number;
}

export const BPIDetailedCard: React.FC<BPIDetailedCardProps> = ({
  score,
  breakdown,
  rank,
  level,
  percentile
}) => {
  const clampedScore = Math.min(100, Math.max(0, Math.round(score)));

  // Determine rank color
  const getRankColor = () => {
    if (clampedScore >= 90) return 'from-purple-500 to-pink-500'; // Diamond
    if (clampedScore >= 80) return 'from-yellow-400 to-yellow-600'; // Platinum
    if (clampedScore >= 70) return 'from-yellow-500 to-yellow-700'; // Gold
    if (clampedScore >= 60) return 'from-gray-300 to-gray-400'; // Silver
    return 'from-amber-600 to-amber-800'; // Bronze
  };

  // Determine rank name
  const getRankName = () => {
    if (clampedScore >= 90) return 'Diamond';
    if (clampedScore >= 80) return 'Platinum';
    if (clampedScore >= 70) return 'Gold';
    if (clampedScore >= 60) return 'Silver';
    return 'Bronze';
  };

  return (
    <div className={`bg-gradient-to-br ${getRankColor()} text-white rounded-xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold opacity-90">Barber Performance Index</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
              {rank || getRankName()}
            </span>
            {level && (
              <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                Level {level}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-bold">{clampedScore}</div>
          <div className="text-sm opacity-90">out of 100</div>
          {percentile && (
            <div className="text-xs mt-1 bg-white bg-opacity-20 px-2 py-1 rounded inline-block">
              Top {100 - percentile}%
            </div>
          )}
        </div>
      </div>

      {/* Metric Breakdown */}
      <div className="space-y-2 mt-4">
        <MetricBar label="Service Count" value={breakdown.serviceCount} weight="25%" />
        <MetricBar label="Revenue" value={breakdown.revenue} weight="25%" />
        <MetricBar label="Speed" value={breakdown.speed} weight="20%" />
        <MetricBar label="Satisfaction" value={breakdown.satisfaction} weight="20%" />
        <MetricBar label="Rebooking Rate" value={breakdown.rebooking} weight="10%" />
      </div>
    </div>
  );
};

/**
 * Helper component for metric progress bars
 */
const MetricBar: React.FC<{ label: string; value: number; weight: string }> = ({
  label,
  value,
  weight
}) => {
  const clampedValue = Math.min(100, Math.max(0, value));

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="opacity-75">
          {Math.round(clampedValue)}% <span className="opacity-60">({weight})</span>
        </span>
      </div>
      <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
        <div
          className="bg-white rounded-full h-2 transition-all duration-300"
          style={{ width: `${clampedValue}%` }}
        />
      </div>
    </div>
  );
};
